import React from 'react'
import {useIntl} from 'react-intl'

import AttendanceV2StatusBadge from './AttendanceV2StatusBadge'

type ColumnDef = {
  key: string
  label: string
  isStatus?: boolean
  format?: (value: unknown) => string
}

type AttendanceV2DataTableProps = {
  columns: ColumnDef[]
  rows: Record<string, unknown>[]
  totalRows?: number
  theme: 'light' | 'dark'
}

const INITIAL_VISIBLE_ROWS = 8

function formatCellValue(value: unknown, col: ColumnDef, theme: 'light' | 'dark'): React.ReactNode {
  if (value == null || value === '') return '—'
  if (col.isStatus && typeof value === 'string') {
    return <AttendanceV2StatusBadge status={value} theme={theme} />
  }
  if (col.format) return col.format(value)
  if (typeof value === 'number') {
    return Number.isInteger(value) ? String(value) : String(Math.round(value * 10) / 10)
  }
  return String(value)
}

const AttendanceV2DataTable: React.FC<AttendanceV2DataTableProps> = ({
  columns,
  rows,
  totalRows,
  theme,
}) => {
  const intl = useIntl()
  const rowTotal = totalRows ?? rows.length

  if (!rows.length) return null

  const borderColor = theme === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'
  const headerBg = theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)'
  const textColor = theme === 'dark' ? '#ffffff' : '#212529'
  const mutedColor = theme === 'dark' ? 'rgba(255,255,255,0.65)' : '#6c757d'

  return (
    <div>
      {rowTotal > INITIAL_VISIBLE_ROWS ? (
        <div className='mb-1' style={{fontSize: '0.75rem', color: mutedColor}}>
          {intl.formatMessage(
            {id: 'CHATBOT.ATTENDANCE.RESULTS_SHOWING_ROWS'},
            {shown: Math.min(rows.length, rowTotal), total: rowTotal}
          )}
        </div>
      ) : null}
      <div
        className='table-responsive mt-2'
        style={{borderRadius: 8, border: `1px solid ${borderColor}`}}
      >
        <table className='table table-sm mb-0' style={{fontSize: '0.8rem', color: textColor}}>
          <thead style={{backgroundColor: headerBg}}>
            <tr>
              {columns.map((col) => (
                <th key={col.key} scope='col' className='px-2 py-2'>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col.key} className='px-2 py-1 align-middle'>
                    {col.isStatus && typeof row[col.key] === 'string' ? (
                      <AttendanceV2StatusBadge status={row[col.key] as string} theme={theme} />
                    ) : (
                      formatCellValue(row[col.key], col, theme)
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export type {ColumnDef}
export {INITIAL_VISIBLE_ROWS}
export default AttendanceV2DataTable
